/**
 * CRM Task React Query Hooks
 * Phase 7 - Hooks for task data fetching and mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { taskApi, TaskFilters, CreateTaskInput, UpdateTaskInput, LeadAssignmentFilters } from '../api/taskApi';

// Query Keys
export const taskQueryKeys = {
  all: ['crm-tasks'] as const,
  lists: () => [...taskQueryKeys.all, 'list'] as const,
  list: (filters: TaskFilters) => [...taskQueryKeys.lists(), filters] as const,
  details: () => [...taskQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...taskQueryKeys.details(), id] as const,
  summary: () => [...taskQueryKeys.all, 'summary'] as const,
  calendar: (startDate: string, endDate: string, assignedToUserId?: string) =>
    [...taskQueryKeys.all, 'calendar', startDate, endDate, assignedToUserId] as const,
  assignees: () => [...taskQueryKeys.all, 'assignees'] as const,
  // Lead Assignment
  leadAssignment: () => [...taskQueryKeys.all, 'lead-assignment'] as const,
  unassignedLeads: (filters?: LeadAssignmentFilters) => [...taskQueryKeys.leadAssignment(), 'unassigned', filters] as const,
  assignedLeads: (filters?: LeadAssignmentFilters) => [...taskQueryKeys.leadAssignment(), 'assigned', filters] as const,
  leadAssignmentSummary: () => [...taskQueryKeys.leadAssignment(), 'summary'] as const,
};

// ===== QUERY HOOKS =====

/**
 * Get tasks with filters
 */
export const useTasks = (filters: TaskFilters = {}) => {
  return useQuery({
    queryKey: taskQueryKeys.list(filters),
    queryFn: () => taskApi.getTasks(filters),
  });
};

/**
 * Get task by ID
 */
export const useTask = (id: string | null) => {
  return useQuery({
    queryKey: taskQueryKeys.detail(id || ''),
    queryFn: () => taskApi.getTaskById(id!),
    enabled: !!id,
  });
};

/**
 * Get task summary statistics
 */
export const useTasksSummary = () => {
  return useQuery({
    queryKey: taskQueryKeys.summary(),
    queryFn: taskApi.getTasksSummary,
  });
};

/**
 * Get tasks for calendar view
 */
export const useTasksCalendar = (startDate: string, endDate: string, assignedToUserId?: string) => {
  return useQuery({
    queryKey: taskQueryKeys.calendar(startDate, endDate, assignedToUserId),
    queryFn: () => taskApi.getTasksCalendar(startDate, endDate, assignedToUserId),
    enabled: !!startDate && !!endDate,
  });
};

/**
 * Get available task assignees
 */
export const useTaskAssignees = () => {
  return useQuery({
    queryKey: taskQueryKeys.assignees(),
    queryFn: taskApi.getTaskAssignees,
  });
};

// ===== MUTATION HOOKS =====

/**
 * Create a new task
 */
export const useCreateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTaskInput) => taskApi.createTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.summary() });
    },
  });
};

/**
 * Update a task
 */
export const useUpdateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateTaskInput & { id: string }) =>
      taskApi.updateTask(id, data),
    onSuccess: (updatedTask) => {
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.summary() });
      queryClient.setQueryData(taskQueryKeys.detail(updatedTask.id), updatedTask);
    },
  });
};

/**
 * Delete a task
 */
export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => taskApi.deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.summary() });
    },
  });
};

/**
 * Update task status
 */
export const useUpdateTaskStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, outcome, notes }: { id: string; status: string; outcome?: string; notes?: string }) =>
      taskApi.updateTaskStatus(id, status, outcome, notes),
    onSuccess: (updatedTask) => {
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.summary() });
      queryClient.setQueryData(taskQueryKeys.detail(updatedTask.id), updatedTask);
    },
  });
};

/**
 * Bulk update task status
 */
export const useBulkUpdateTaskStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskIds, status }: { taskIds: string[]; status: string }) =>
      taskApi.bulkUpdateTaskStatus(taskIds, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.summary() });
    },
  });
};

// ===== COMBINED DATA HOOK =====

/**
 * Get all task data needed for the Task Management tab
 */
export const useTaskManagementData = (filters: TaskFilters = {}) => {
  const tasksQuery = useTasks(filters);
  const summaryQuery = useTasksSummary();
  const assigneesQuery = useTaskAssignees();

  return {
    tasks: tasksQuery.data,
    summary: summaryQuery.data,
    assignees: assigneesQuery.data,
    isLoading: tasksQuery.isLoading || summaryQuery.isLoading || assigneesQuery.isLoading,
    isError: tasksQuery.isError || summaryQuery.isError || assigneesQuery.isError,
    errors: {
      tasks: tasksQuery.error,
      summary: summaryQuery.error,
      assignees: assigneesQuery.error,
    },
  };
};

// ===== LEAD ASSIGNMENT HOOKS =====

/**
 * Get unassigned leads
 */
export const useUnassignedLeads = (filters?: LeadAssignmentFilters) => {
  return useQuery({
    queryKey: taskQueryKeys.unassignedLeads(filters),
    queryFn: () => taskApi.getUnassignedLeads(filters),
  });
};

/**
 * Get assigned leads
 */
export const useAssignedLeads = (filters?: LeadAssignmentFilters) => {
  return useQuery({
    queryKey: taskQueryKeys.assignedLeads(filters),
    queryFn: () => taskApi.getAssignedLeads(filters),
  });
};

/**
 * Get lead assignment summary
 */
export const useLeadAssignmentSummary = () => {
  return useQuery({
    queryKey: taskQueryKeys.leadAssignmentSummary(),
    queryFn: taskApi.getLeadAssignmentSummary,
  });
};

/**
 * Assign a lead to a staff member
 */
export const useAssignLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, assignedToUserId }: { leadId: string; assignedToUserId: string }) =>
      taskApi.assignLead(leadId, assignedToUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.leadAssignment() });
    },
  });
};

/**
 * Bulk assign leads to a staff member
 */
export const useBulkAssignLeads = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadIds, assignedToUserId }: { leadIds: string[]; assignedToUserId: string }) =>
      taskApi.bulkAssignLeads(leadIds, assignedToUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.leadAssignment() });
    },
  });
};
